import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BookPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useDraft } from "@/components/import/DraftContext";
import { Textarea } from "@/components/ui/textarea";

const KINDS = [["keyword", "Keyword"], ["ability_word", "Ability word"], ["mechanic", "Mechanic"]];
const BADGE = {
  exists: ["Already in Mechanics", "border-emerald-600/50 text-emerald-300"],
  added: ["Added · in review", "border-aether-400/60 text-aether-300"],
  official: ["Official keyword", "border-slate-600 text-slate-400"],
};

function MechanicRow({ m, edit, setEdit, picked, toggle }) {
  const e = edit[m.key] || {};
  const set = (k, v) => setEdit((s) => ({ ...s, [m.key]: { ...e, [k]: v } }));
  const badge = BADGE[m.status];
  return (
    <li className="space-y-2 py-4" data-testid={`import-mechanic-${m.key}`}>
      <div className="flex flex-wrap items-center gap-3">
        {m.status === "new" && <input type="checkbox" checked={picked} onChange={toggle} data-testid={`import-mechanic-pick-${m.key}`} />}
        <p className="font-heading text-base text-amber-100">{m.name}</p>
        {m.file_kind && <span className="font-mono text-[10px] text-slate-500">{m.file_kind}</span>}
        {badge && <span className={`rounded border px-2 py-0.5 font-mono text-[10px] uppercase ${badge[1]}`}>{badge[0]}</span>}
        {m.mechanic_id && <Link to={`/mechanics/${m.mechanic_id}`} className="text-xs text-aether-300 hover:underline" data-testid={`import-mechanic-link-${m.key}`}>Open</Link>}
        <span className="text-xs text-slate-500">{m.used_by.length ? `on ${m.used_by.length} card(s)` : "listed in the file only"}</span>
      </div>
      {m.status === "new" && picked && (
        <div className="grid gap-3 sm:grid-cols-[160px_1fr]">
          <select className="field" value={e.kind || m.kind} onChange={(ev) => set("kind", ev.target.value)} data-testid={`import-mechanic-kind-${m.key}`}>
            {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input className="field" maxLength={500} placeholder="Reminder text (optional)" value={e.reminder_text ?? m.reminder_text}
            onChange={(ev) => set("reminder_text", ev.target.value)} data-testid={`import-mechanic-reminder-${m.key}`} />
          <Textarea className="field text-xs sm:col-span-2" rows={4} maxLength={4000} value={e.definition ?? m.definition}
            onChange={(ev) => set("definition", ev.target.value)} data-testid={`import-mechanic-definition-${m.key}`} />
        </div>
      )}
    </li>
  );
}

export function MechanicsPanel() {
  const { draft, id, refresh } = useDraft();
  const list = useMemo(() => draft.mechanics || [], [draft.mechanics]);
  const fresh = list.filter((m) => m.status === "new");
  const [picked, setPicked] = useState(() => new Set());
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState(false);
  if (!list.length) return null;
  const toggle = (key) => setPicked((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const add = async () => {
    setBusy(true);
    try {
      const items = [...picked].filter((k) => fresh.some((m) => m.key === k)).map((key) => ({ key, ...edit[key] }));
      const { data } = await api.post(`/imports/${id}/mechanics`, { items });
      const ok = data.results.filter((r) => r.ok).length;
      data.results.filter((r) => !r.ok).forEach((r) => toast.error(`${r.key}: ${r.error}`));
      if (ok) toast.success(`${ok} mechanic(s) added to the Mechanics section for review`);
      setPicked(new Set());
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <details className="panel p-5" open={fresh.length > 0} data-testid="import-mechanics-panel">
      <summary className="cursor-pointer">
        <span className="eyebrow">Mechanics in this file</span>
        <span className="ml-3 text-sm text-slate-300" data-testid="import-mechanics-count">{fresh.length} new · {list.length - fresh.length} already known</span>
      </summary>
      <p className="mt-3 text-sm text-slate-400">Cards always keep their mechanics. Tick the new ones you also want in the site's Mechanics section; each goes through the usual mechanic review before it's public.</p>
      {fresh.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-3">
          <button className="btn-ghost px-2 py-1 text-xs" onClick={() => setPicked(new Set(fresh.map((m) => m.key)))} data-testid="import-mechanics-select-all">Select all new</button>
          <button className="btn-gold" disabled={!picked.size || busy} onClick={add} data-testid="import-mechanics-add">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookPlus className="h-4 w-4" />}Add {picked.size} to Mechanics section
          </button>
        </div>
      )}
      <ul className="divide-y divide-[#2A3447]">
        {list.map((m) => <MechanicRow key={m.key} m={m} edit={edit} setEdit={setEdit} picked={picked.has(m.key)} toggle={() => toggle(m.key)} />)}
      </ul>
    </details>
  );
}
